// SPDX-License-Identifier: AGPL-3.0-or-later

import statusStyles from '@app/features/app/components/dialogs/components/plutonium/PurchaseHistoryStatus.module.css';
import {availableWallets, DaoIWallet} from '@app/features/daoi/bridge/DaoIWallet';
import {type DaoIAssets, getDaoIData, useDaoI} from '@app/features/daoi/state/DaoIData';
import Theme from '@app/features/theme/state/Theme';
import {Button} from '@app/features/ui/button/Button';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import {SegmentedTabs} from '@app/features/ui/segmented_tabs/SegmentedTabs';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import {openUserSettingsModal} from '@app/features/user/commands/UserSettingsModalCommands';
import {
	AccountSecurityCard,
	AccountSecurityEmptyRow,
	AccountSecurityRow,
} from '@app/features/user/components/modals/tabs/account_security_tab/AccountSecurityCard';
import {ProfileCardContent} from '@app/features/user/components/profile/profile_card/ProfileCardContent';
import {ProfileCardLayout} from '@app/features/user/components/profile/profile_card/ProfileCardLayout';
import {ProfileCardUserInfo} from '@app/features/user/components/profile/profile_card/ProfileCardUserInfo';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {
	ArrowSquareOutIcon,
	CopyIcon,
	CrownIcon,
	EyeIcon,
	EyeSlashIcon,
	GearIcon,
	WalletIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const CHAINS = [
	[56, 'BNB Chain'],
	[1, 'Ethereum'],
	[8453, 'Base'],
	[42161, 'Arbitrum'],
	[59144, 'Linea'],
	[137, 'Polygon'],
	[196, 'X Layer'],
] as const;
const shortAddress = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`;
const usd = (value: number) =>
	new Intl.NumberFormat('zh-CN', {style: 'currency', currency: 'USD', maximumFractionDigits: 2}).format(value);

export const DaoIMePage = observer(() => {
	const {me, openBusiness, refresh: refreshAccount} = useDaoI();
	const user = Users.currentUser;
	const [chainId, setChainId] = useState(56);
	const [assets, setAssets] = useState<DaoIAssets | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hidden, setHidden] = useState(() => {
		try {
			return localStorage.getItem('daoi:hide-balances') === 'true';
		} catch {
			return false;
		}
	});
	const [walletOpen, setWalletOpen] = useState(false);
	const [walletBusy, setWalletBusy] = useState(false);
	const [walletError, setWalletError] = useState<string | null>(null);
	const [refresh, setRefresh] = useState(0);
	const [copied, setCopied] = useState(false);
	useEffect(() => {
		const controller = new AbortController();
		setAssets(null);
		setError(null);
		if (!me?.wallet_address) {
			setLoading(false);
			if (me) setError('账号钱包尚未绑定，资产暂不可读取。');
			return () => controller.abort();
		}
		setLoading(true);
		void getDaoIData<DaoIAssets>(`/daoi/wallet-assets?chain_id=${chainId}`, controller.signal)
			.then((result) => {
				if (!controller.signal.aborted) setAssets(result);
			})
			.catch(() => {
				if (!controller.signal.aborted) setError('资产暂时无法读取，请稍后重试。');
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, [chainId, me, refresh]);
	if (!user) return null;
	const wallet = me?.wallet_address;
	const toggleHidden = () => {
		const next = !hidden;
		setHidden(next);
		try {
			localStorage.setItem('daoi:hide-balances', String(next));
		} catch {
			/* Preference remains available for this visit. */
		}
	};
	const setTheme = async (theme: 'dark' | 'light') => {
		Theme.setTheme(theme === 'dark' ? ThemeTypes.DARK : ThemeTypes.LIGHT);
		try {
			await UserSettingsCommands.update({theme: theme === 'dark' ? ThemeTypes.DARK : ThemeTypes.LIGHT});
		} catch {
			setError('主题已在本机切换，跨设备保存暂时失败。');
		}
	};
	const copyWallet = () => {
		if (!wallet) return;
		void navigator.clipboard
			.writeText(wallet)
			.then(() => {
				setCopied(true);
				window.setTimeout(() => setCopied(false), 1500);
			})
			.catch(() => setWalletError('复制失败，请从钱包中复制地址。'));
	};
	const connectWallet = async (provider: Parameters<typeof DaoIWallet.connect>[0]) => {
		setWalletBusy(true);
		setWalletError(null);
		try {
			await DaoIWallet.connect(provider);
		} catch (failure) {
			setWalletError(failure instanceof Error ? failure.message : '钱包连接失败，请重试。');
		} finally {
			setWalletBusy(false);
		}
	};
	const valuation = hidden
		? '••••••'
		: error && !assets
			? '暂不可用'
			: !me || loading
				? '读取中…'
				: assets?.estimated_total_usd != null
					? usd(assets.estimated_total_usd)
					: '暂不可用';
	return (
		<main className="daoi-me-page" data-flx="daoi.dao-i-me-page.daoi-me-page">
			<header className="daoi-page-heading" data-flx="daoi.dao-i-me-page.daoi-page-heading">
				<h1 data-flx="daoi.dao-i-me-page.h1">我的</h1>
				<Tooltip text="打开设置" data-flx="daoi.dao-i-me-page.settings-tooltip">
					<Button
						variant="ghost"
						square
						icon={<GearIcon size={20} data-flx="daoi.dao-i-me-page.settings-icon" />}
						aria-label="打开设置"
						onClick={openUserSettingsModal}
						data-flx="daoi.dao-i-me-page.settings"
					/>
				</Tooltip>
			</header>
			<div className="daoi-me-grid" data-flx="daoi.dao-i-me-page.daoi-me-grid">
				<section className="daoi-profile-block" aria-label="个人资料" data-flx="daoi.dao-i-me-page.profile">
					<ProfileCardLayout
						borderColor="var(--background-modifier-accent)"
						style={{width: '100%'}}
						data-flx="daoi.dao-i-me-page.profile-card"
					>
						<ProfileCardContent data-flx="daoi.dao-i-me-page.profile-content">
							<StatusAwareAvatar user={user} size={80} disableStatusTooltip data-flx="daoi.dao-i-me-page.avatar" />
							<ProfileCardUserInfo
								user={user}
								displayName={NicknameUtils.getDisplayName(user)}
								isClickable={false}
								data-flx="daoi.dao-i-me-page.user-info"
							/>
							<span
								className={
									me?.partner_status === 'unavailable'
										? statusStyles.muted
										: me?.partner.eligible
											? statusStyles.pending
											: statusStyles.neutral
								}
								data-flx="daoi.dao-i-me-page.identity"
							>
								{!me ? (
									'身份读取中…'
								) : me.partner_status === 'unavailable' ? (
									'身份暂不可用'
								) : me?.partner.eligible ? (
									<>
										<CrownIcon size={14} weight="fill" data-flx="daoi.dao-i-me-page.partner-icon" />
										<span data-flx="daoi.dao-i-me-page.partner-label">超级合伙人</span>
									</>
								) : (
									'普通用户'
								)}
							</span>
							{wallet && (
								<div className="daoi-profile-meta" data-flx="daoi.dao-i-me-page.bound-wallet">
									<span className="daoi-muted" data-flx="daoi.dao-i-me-page.bound-label">
										绑定钱包
									</span>
									<Tooltip text={wallet} data-flx="daoi.dao-i-me-page.wallet-tooltip">
										<Button
											variant="secondary"
											compact
											leftIcon={<CopyIcon size={16} data-flx="daoi.dao-i-me-page.copy-icon" />}
											onClick={copyWallet}
											aria-label="复制绑定钱包地址"
											data-flx="daoi.dao-i-me-page.copy-wallet"
										>
											{copied ? '已复制' : shortAddress(wallet)}
										</Button>
									</Tooltip>
									{copied && (
										<span role="status" className="daoi-muted" data-flx="daoi.dao-i-me-page.copy-status">
											地址已复制
										</span>
									)}
								</div>
							)}
							<p className="daoi-muted daoi-profile-note" data-flx="daoi.dao-i-me-page.profile-note">
								聊天账号与钱包一一绑定。资格来自 DAOI 平台实时查询。
							</p>
						</ProfileCardContent>
					</ProfileCardLayout>
				</section>
				<div className="daoi-assets-column" data-flx="daoi.dao-i-me-page.daoi-assets-column">
					<AccountSecurityCard
						title="我的资产"
						description={
							!assets
								? '按当前网络读取钱包资产'
								: assets.valuation_complete
									? '当前网络支持币种的估值'
									: '已估值资产 · 部分资产可能缺少价格'
						}
						action={
							<Tooltip text={hidden ? '显示资产金额' : '隐藏资产金额'} data-flx="daoi.dao-i-me-page.balance-tooltip">
								<Button
									variant="ghost"
									square
									icon={
										hidden ? (
											<EyeSlashIcon size={18} data-flx="daoi.dao-i-me-page.eye-hidden" />
										) : (
											<EyeIcon size={18} data-flx="daoi.dao-i-me-page.eye" />
										)
									}
									onClick={toggleHidden}
									aria-label={hidden ? '显示资产金额' : '隐藏资产金额'}
									data-flx="daoi.dao-i-me-page.toggle-balances"
								/>
							</Tooltip>
						}
						data-flx="daoi.dao-i-me-page.asset-card"
					>
						<AccountSecurityRow label="资产估值" data-flx="daoi.dao-i-me-page.valuation-row">
							<strong data-flx="daoi.dao-i-me-page.valuation">{valuation}</strong>
						</AccountSecurityRow>
						<AccountSecurityRow label="网络" data-flx="daoi.dao-i-me-page.network-row">
							<div className="daoi-chain-picker" data-flx="daoi.dao-i-me-page.chain-picker">
								<Combobox
									value={chainId}
									options={CHAINS.map(([value, label]) => ({value: Number(value), label}))}
									onChange={setChainId}
									aria-label="资产网络"
									isSearchable={false}
									density="compact"
									data-flx="daoi.dao-i-me-page.chain-combobox"
								/>
							</div>
							<Button
								variant="secondary"
								compact
								disabled={loading}
								onClick={() => {
									refreshAccount();
									setRefresh((value) => value + 1);
								}}
								data-flx="daoi.dao-i-me-page.refresh"
							>
								刷新
							</Button>
						</AccountSecurityRow>
					</AccountSecurityCard>
					<AccountSecurityCard title="我持有的币" description="支持范围内资产" data-flx="daoi.dao-i-me-page.holdings">
						{loading && (
							<AccountSecurityEmptyRow data-flx="daoi.dao-i-me-page.loading-row">
								<span role="status" data-flx="daoi.dao-i-me-page.loading">
									正在读取链上余额…
								</span>
							</AccountSecurityEmptyRow>
						)}
						{error && (
							<AccountSecurityEmptyRow data-flx="daoi.dao-i-me-page.error-row">
								<span className="daoi-error" role="alert" data-flx="daoi.dao-i-me-page.error">
									{error}
								</span>
							</AccountSecurityEmptyRow>
						)}
						{assets?.assets.map((asset) => (
							<AccountSecurityRow
								key={`${asset.chain_id}:${asset.address}`}
								description={asset.name}
								label={
									<span className="daoi-asset-identity" data-flx="daoi.dao-i-me-page.asset-identity">
										<span className="daoi-token-mark" data-flx="daoi.dao-i-me-page.token-mark">
											{asset.logo_uri ? (
												<img
													src={new URL(asset.logo_uri, 'https://daoi.ai').toString()}
													alt=""
													loading="lazy"
													onError={(event) => {
														event.currentTarget.style.display = 'none';
													}}
													data-flx="daoi.dao-i-me-page.token-logo"
												/>
											) : (
												asset.symbol.slice(0, 2)
											)}
										</span>
										{asset.symbol}
									</span>
								}
								data-flx="daoi.dao-i-me-page.asset-row"
							>
								<div className="daoi-asset-value" data-flx="daoi.dao-i-me-page.asset-value">
									<strong data-flx="daoi.dao-i-me-page.asset-balance">
										{hidden ? '••••' : asset.balance === null ? '暂不可用' : asset.balance}
									</strong>
									<span className="daoi-muted" data-flx="daoi.dao-i-me-page.asset-price">
										{hidden ? '••••' : asset.value_usd !== null ? usd(asset.value_usd) : '价格暂不可用'}
									</span>
								</div>
							</AccountSecurityRow>
						))}
						{!loading && !error && assets?.assets.length === 0 && (
							<AccountSecurityEmptyRow data-flx="daoi.dao-i-me-page.empty">
								当前网络没有可展示的支持币种。
							</AccountSecurityEmptyRow>
						)}
					</AccountSecurityCard>
				</div>
				<aside
					className="daoi-profile-tools"
					aria-label="账号与钱包管理"
					data-flx="daoi.dao-i-me-page.daoi-profile-tools"
				>
					<AccountSecurityCard
						title="钱包管理"
						action={
							<Button
								variant="secondary"
								compact
								onClick={() => setWalletOpen(!walletOpen)}
								aria-expanded={walletOpen}
								data-flx="daoi.dao-i-me-page.wallet-expand"
							>
								{walletOpen ? '收起' : '管理'}
							</Button>
						}
						description={
							DaoIWallet.account
								? `${shortAddress(DaoIWallet.account)} · ${DaoIWallet.chainId ?? '未知网络'}`
								: '钱包尚未连接'
						}
						data-flx="daoi.dao-i-me-page.wallet-management"
					>
						{walletOpen && (
							<>
								{(DaoIWallet.error || walletError) && (
									<AccountSecurityEmptyRow data-flx="daoi.dao-i-me-page.wallet-error-row">
										<p role="alert" className="daoi-error" data-flx="daoi.dao-i-me-page.wallet-error">
											{DaoIWallet.error || walletError}
										</p>
									</AccountSecurityEmptyRow>
								)}
								{availableWallets().map((entry, index) => (
									<AccountSecurityRow
										key={`${entry.name}:${index}`}
										label={entry.name}
										data-flx="daoi.dao-i-me-page.wallet-provider-row"
									>
										<Button
											variant="secondary"
											compact
											disabled={walletBusy}
											onClick={() => void connectWallet(entry.provider)}
											leftIcon={<WalletIcon size={16} data-flx="daoi.dao-i-me-page.wallet-icon" />}
											data-flx="daoi.dao-i-me-page.wallet-connect"
										>
											连接
										</Button>
									</AccountSecurityRow>
								))}
								{availableWallets().length === 0 && (
									<AccountSecurityEmptyRow data-flx="daoi.dao-i-me-page.no-wallet">
										请在已安装钱包扩展的浏览器或受支持的钱包浏览器中打开。
									</AccountSecurityEmptyRow>
								)}
								<AccountSecurityRow
									label="账号绑定"
									description="断开连接不会解除账号绑定。"
									data-flx="daoi.dao-i-me-page.wallet-disconnect-row"
								>
									{DaoIWallet.account && (
										<Button
											variant="ghost"
											compact
											onClick={() => DaoIWallet.disconnect()}
											data-flx="daoi.dao-i-me-page.wallet-disconnect"
										>
											断开连接
										</Button>
									)}
								</AccountSecurityRow>
							</>
						)}
					</AccountSecurityCard>
					<AccountSecurityCard title="账号" data-flx="daoi.dao-i-me-page.account-card">
						<AccountSecurityRow label="合伙人计划" data-flx="daoi.dao-i-me-page.partner-row">
							<Button
								variant="secondary"
								compact
								onClick={() => openBusiness('partner')}
								data-flx="daoi.dao-i-me-page.partner-open"
							>
								查看
							</Button>
						</AccountSecurityRow>
						<AccountSecurityRow label="设置" data-flx="daoi.dao-i-me-page.settings-row">
							<Button
								variant="secondary"
								compact
								onClick={openUserSettingsModal}
								data-flx="daoi.dao-i-me-page.settings-open"
							>
								打开
							</Button>
						</AccountSecurityRow>
					</AccountSecurityCard>
					<AccountSecurityCard title="外观" data-flx="daoi.dao-i-me-page.appearance">
						<SegmentedTabs
							tabs={[
								{id: 'dark', label: '深色'},
								{id: 'light', label: '浅色'},
							]}
							selectedTab={Theme.effectiveTheme === ThemeTypes.LIGHT ? 'light' : 'dark'}
							onTabChange={(value) => void setTheme(value)}
							ariaLabel="选择主题"
							data-flx="daoi.dao-i-me-page.theme-tabs"
						/>
					</AccountSecurityCard>
					<a
						className="daoi-website-link"
						href="https://daoi.ai"
						target="_blank"
						rel="noreferrer"
						data-flx="daoi.dao-i-me-page.website-link"
					>
						<span data-flx="daoi.dao-i-me-page.website-label">DAOI 官网</span>
						<ArrowSquareOutIcon size={16} data-flx="daoi.dao-i-me-page.website-icon" />
					</a>
				</aside>
			</div>
		</main>
	);
});
